import { useState } from "react";
import { Link, Outlet, useParams, useRouteContext } from "@tanstack/react-router";
import { CreditCard, Menu, QrCode, Settings, ShieldCheck } from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { cn } from "@qrody/utils";
import { useTranslations } from "@/i18n/IntlProvider";
import type { MeResponse } from "@/auth/me";
import { AuthMenu } from "@/components/AuthMenu";
import {
  AppSidebarSlotProvider,
  useAppSidebarSlot,
} from "@/components/app-sidebar-slot";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

/**
 * Layout chrome for the authenticated surface.
 *
 * UX directives carried forward from the 2026-05-11 polish session
 * (commits 04eee30 + 6445b58):
 *   - sidebar slot on every viewport, never collapsed by default;
 *   - no "Meine Codes" button in the header — the sidebar nav replaces it.
 *
 * The admin link visibility now derives from the principal's `roles`
 * array (look for `system-admin`). The legacy email-endsWith heuristic
 * was a workaround while the role mapping was still flaky; with the BFF
 * /auth/me response stable we can trust the roles.
 */
export function AppShell() {
  const { locale } = useParams({ strict: false }) as { locale?: string };
  const lang = locale ?? "de";
  const t = useTranslations();
  const ctx = useRouteContext({ strict: false }) as { me?: MeResponse | null };
  const principal = ctx?.me ?? null;
  const isAdmin = principal?.roles?.includes("system-admin") ?? false;

  const items: ReadonlyArray<NavItem> = [
    { to: "/$locale/codes", icon: QrCode, label: t("app.nav.codes") },
    { to: "/$locale/billing", icon: CreditCard, label: t("app.nav.billing") },
    { to: "/$locale/settings/account", icon: Settings, label: t("app.nav.settings") },
    ...(isAdmin
      ? [{ to: "/$locale/admin/accounts", icon: ShieldCheck, label: t("app.nav.admin") }]
      : []),
  ];

  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <AppSidebarSlotProvider>
      <div className="grid min-h-dvh grid-rows-[auto_1fr] bg-background text-foreground">
        <Header
          lang={lang}
          principal={principal}
          menuLabel={t("app.nav.openMenu")}
          onMenuClick={() => setMobileNavOpen(true)}
        />
        {/*
         * Sidebar collapses below `lg` (1024 px viewport): on narrow
         * screens the Hamburger in the header opens a Sheet-drawer with
         * the same nav + slot, so the editor surface gets the full
         * width on tablet / mobile.
         */}
        <div className="grid grid-cols-1 lg:grid-cols-[16rem_1fr]">
          <Sidebar lang={lang} items={items} className="hidden lg:flex" />
          <main className="min-w-0 p-6">
            <Outlet />
          </main>
        </div>
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className="w-72 p-4 sm:max-w-sm">
            <SheetHeader className="px-0">
              <SheetTitle className="sr-only">{t("app.nav.openMenu")}</SheetTitle>
            </SheetHeader>
            <Sidebar
              lang={lang}
              items={items}
              onNavigate={() => setMobileNavOpen(false)}
            />
          </SheetContent>
        </Sheet>
      </div>
    </AppSidebarSlotProvider>
  );
}

type NavItem = {
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
};

function Header({
  lang,
  principal,
  menuLabel,
  onMenuClick,
}: {
  lang: string;
  principal: MeResponse | null;
  menuLabel: string;
  onMenuClick: () => void;
}) {
  return (
    <header className="flex items-center justify-between gap-4 border-b border-border bg-card px-4 py-3 md:px-6">
      <div className="flex items-center gap-2">
        {/*
         * Hamburger surfaces only below `lg` — once the sidebar is in
         * the grid (`lg+`) the button is redundant.
         */}
        <button
          type="button"
          onClick={onMenuClick}
          aria-label={menuLabel}
          className="-ml-1 inline-flex items-center justify-center rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground lg:hidden"
        >
          <Menu aria-hidden className="size-5" />
        </button>
        <Link
          to="/$locale"
          params={{ locale: lang }}
          className="flex items-center gap-2 text-lg font-semibold tracking-tight"
        >
          <Logo aria-hidden className="size-6" />
          <span>qrody</span>
        </Link>
      </div>
      <AuthMenu lang={lang} principal={principal} />
    </header>
  );
}

function Sidebar({
  lang,
  items,
  className,
  onNavigate,
}: {
  lang: string;
  items: ReadonlyArray<NavItem>;
  className?: string;
  /** When set, fires on every nav-link click — used by the mobile
   *  Sheet variant to close itself after navigation. */
  onNavigate?: () => void;
}) {
  // Per-route slot content — sub-iter #2 lights this up on /codes/$id to
  // host the identity card + lifecycle CTAs + mode toggle next to the
  // nav menu (legacy "Lifecycle in sidebar" UX from web/, commits
  // eab1d60 + 806fb01 + df97a37).
  const slot = useAppSidebarSlot();
  return (
    <aside
      aria-label="Dashboard"
      className={cn(
        "flex flex-col gap-4 border-r border-border bg-card p-4 text-sm",
        className,
      )}
    >
      <nav>
        <ul className="flex flex-col gap-1">
          {items.map(({ to, icon: Icon, label }) => (
            <li key={to}>
              {/*
               * TanStack-Router's typed <Link> would need the route tree
               * propagated through a generic — we cast to never for now
               * so each nav item stays a single readable line. Sub-iter
               * #1-#5 swap this for typed `to` literals once each route
               * lands its real component.
               */}
              <Link
                to={to as never}
                params={{ locale: lang } as never}
                onClick={onNavigate}
                className={cn(
                  "flex items-center gap-2 rounded-md px-3 py-2 font-medium text-muted-foreground transition-colors",
                  "hover:bg-accent hover:text-accent-foreground",
                  "[&.active]:bg-primary [&.active]:text-primary-foreground",
                )}
                activeOptions={{ exact: false }}
              >
                <Icon aria-hidden className="size-4" />
                <span>{label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      {slot}
    </aside>
  );
}

/**
 * Mini logo placeholder — a rounded square with the brand "q" glyph.
 * Mirrors web/src/app/icon.tsx (which renders the favicon) just rendered
 * as an inline SVG so we don't pull in next/og for the SPA.
 */
function Logo(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <rect width="64" height="64" rx="12" fill="#0f172a" />
      <text
        x="32"
        y="46"
        textAnchor="middle"
        fontFamily="system-ui, -apple-system, sans-serif"
        fontWeight="800"
        fontSize="48"
        fill="#f8fafc"
        letterSpacing="-2"
      >
        q
      </text>
    </svg>
  );
}
